/**
 * `humanbehavior-js/react/auto` - the auto-updating React integration.
 *
 * Identical surface to `humanbehavior-js/react`, except the recorder is fetched
 * from the CDN at runtime instead of bundled, so a recorder we publish reaches
 * the customer's users on their next page load with no reinstall.
 *
 * Nothing here may import `humanbehavior-js`: that would pull the recorder back
 * into the customer's build and defeat the point. `tests/no-recorder-in-auto-bundle`
 * in the package's unit tests fails the build if it ever creeps back in.
 */

import { HumanBehaviorTracker } from "@humanbehavior/loader";
import { createHumanBehaviorReact } from "./factory";

// The SDK's own logger lives in the recorder, which by definition is not here
// yet. These few messages are developer-facing integration errors, so the
// console is the right destination for them anyway.
const react = createHumanBehaviorReact(HumanBehaviorTracker, {
  /* eslint-disable no-console */
  error: (...args: any[]) => console.error('[HumanBehavior]', ...args),
  warn: (...args: any[]) => console.warn('[HumanBehavior]', ...args),
  debug: () => {},
  /* eslint-enable no-console */
});

export const HumanBehaviorProvider = react.HumanBehaviorProvider;
export const useHumanBehavior = react.useHumanBehavior;
export const useRedaction = react.useRedaction;
export const useUserTracking = react.useUserTracking;
export const HumanBehaviorErrorBoundary = react.HumanBehaviorErrorBoundary;
/** Matches the bundled entry, where this name works as a type as well. */
export type HumanBehaviorErrorBoundary = InstanceType<typeof react.HumanBehaviorErrorBoundary>;

export { captureNextError } from "./factory";
export type { IdentifyUserArgs } from "./factory";

export { HumanBehaviorTracker };
